/**
 * TemplatesPanel — slide-in panel from the inline edge with categorised
 * legal-prompt presets. On pick, returns the template prompt text.
 */
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

const TEMPLATES = [
  {
    category_ar: 'مراجعة العقود',
    category_en: 'Contract review',
    items: [
      { id: 'review_full',    title_ar: 'مراجعة عقد شاملة',     title_en: 'Full contract review',
        prompt_ar: 'راجع هذا العقد بنداً بنداً، حدد المخاطر، البنود المفقودة، وقدم توصيات تحسين موثقة وفق الأنظمة السعودية.',
        prompt_en: 'Review this contract clause-by-clause, flag risks, missing clauses, and recommend documented improvements under Saudi law.' },
      { id: 'review_risks',   title_ar: 'استخراج المخاطر فقط',  title_en: 'Risks only',
        prompt_ar: 'حدد جميع البنود عالية المخاطر في هذا العقد مع شرح سبب الخطورة والمادة النظامية ذات الصلة.',
        prompt_en: 'Identify all high-risk clauses in this contract — explain why and cite the relevant statutory article.' },
      { id: 'review_missing', title_ar: 'البنود المفقودة',      title_en: 'Missing clauses',
        prompt_ar: 'حدد البنود الجوهرية المفقودة من هذا العقد مقارنةً بالنموذج القياسي لهذا النوع من العقود وفق النظام السعودي.',
        prompt_en: 'Identify essential missing clauses vs the Saudi-law standard template for this contract type.' },
    ],
  },
  {
    category_ar: 'صياغة المستندات',
    category_en: 'Drafting',
    items: [
      { id: 'draft_nda',         title_ar: 'اتفاقية سرية (NDA)', title_en: 'Mutual NDA',
        prompt_ar: 'صِغ اتفاقية سرية متبادلة بين [اسم الطرف الأول] و[اسم الطرف الثاني] لغرض [وصف الغرض] وفق الأنظمة السعودية.',
        prompt_en: 'Draft a mutual NDA between [Party A] and [Party B] for [purpose] under Saudi law.' },
      { id: 'draft_employment',  title_ar: 'عقد عمل',           title_en: 'Employment contract',
        prompt_ar: 'صِغ عقد عمل بين [اسم صاحب العمل] و[اسم الموظف] لمنصب [المسمى الوظيفي] براتب [الراتب] وفق نظام العمل السعودي.',
        prompt_en: 'Draft an employment contract between [Employer] and [Employee] for [Role] at SAR [salary] under the Saudi Labor Law.' },
      { id: 'draft_partnership', title_ar: 'عقد شراكة',          title_en: 'Partnership agreement',
        prompt_ar: 'صِغ عقد شراكة تجارية بين [اسم الشريك الأول] و[اسم الشريك الثاني] بنسبة أرباح [النسبة] وفق نظام الشركات السعودي.',
        prompt_en: 'Draft a commercial partnership between [Partner 1] and [Partner 2] at [%/%] profit split under the Saudi Companies Law.' },
    ],
  },
  {
    category_ar: 'الاستفسارات القانونية',
    category_en: 'Legal Q&A',
    items: [
      { id: 'query_labor',       title_ar: 'حقوق العمل',         title_en: 'Labor rights',
        prompt_ar: 'ما هي حقوق الموظف في حالة [وصف الحالة] وفق نظام العمل السعودي؟ مع ذكر المواد النظامية.',
        prompt_en: 'What are the employee rights in [scenario] under the Saudi Labor Law? Cite the articles.' },
      { id: 'query_termination', title_ar: 'إنهاء الخدمة',       title_en: 'End of service',
        prompt_ar: 'ما هي الإجراءات والمستحقات القانونية عند إنهاء خدمة موظف في [الحالة] وفق نظام العمل السعودي؟',
        prompt_en: 'What are the legal procedures and entitlements on terminating an employee in [scenario] under the Saudi Labor Law?' },
      { id: 'query_compliance',  title_ar: 'فحص الامتثال',       title_en: 'Compliance check',
        prompt_ar: 'افحص هذا المستند وحدد مدى توافقه مع [اسم النظام] مع ذكر المواد النظامية لكل نقطة.',
        prompt_en: 'Audit this document for compliance with [law] — cite articles for every point.' },
    ],
  },
];

export function TemplatesPanel({ open, onClose, onPick }) {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const ref = useRef(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!open) return null;

  return (
    <aside
      ref={ref}
      dir={isArabic ? 'rtl' : 'ltr'}
      className="fixed top-[48px] z-40 overflow-y-auto"
      style={{
        [isArabic ? 'left' : 'right']: 0,
        width: '320px',
        height: 'calc(100vh - 48px)',
        background: '#FFFFFF',
        borderInlineStart: '1px solid #E8E8E8',
      }}
      data-testid="templates-panel"
    >
      <div className={cn('flex items-center justify-between px-4 py-3 sticky top-0 z-10', isArabic && 'flex-row-reverse')} style={{ background: '#FFFFFF', borderBottom: '1px solid #E8E8E8' }}>
        <h3 className="font-arabic" style={{ fontSize: '14px', fontWeight: 500, color: '#111111' }}>
          {isArabic ? 'النماذج القانونية' : 'Legal templates'}
        </h3>
        <button onClick={onClose} aria-label="Close" data-testid="templates-panel-close" style={{ color: '#999999' }}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {TEMPLATES.map((cat) => (
        <section key={cat.category_en}>
          <div
            className={cn('uppercase', isArabic ? 'text-right' : 'text-left')}
            style={{ fontSize: '10px', color: '#999999', letterSpacing: '1px', padding: '16px 16px 8px' }}
          >
            {isArabic ? cat.category_ar : cat.category_en}
          </div>
          {cat.items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { onPick(isArabic ? it.prompt_ar : it.prompt_en); onClose(); }}
              className={cn('w-full hover:bg-[#F5F5F5] font-arabic block', isArabic ? 'text-right' : 'text-left')}
              style={{ padding: '10px 16px', borderRadius: '6px', margin: '2px 8px', fontSize: '13px', color: '#111111', cursor: 'pointer', maxWidth: 'calc(100% - 16px)' }}
              data-testid={`templates-panel-item-${it.id}`}
            >
              {isArabic ? it.title_ar : it.title_en}
            </button>
          ))}
        </section>
      ))}
    </aside>
  );
}

export default TemplatesPanel;
